import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";

export interface ProgramDay {
  id: string;
  program_id: string;
  day_number: number;
  name: string | null;
  workout_template_id: string | null;
  is_rest_day: boolean;
  workout_template?: {
    id: string;
    name: string;
    estimated_duration_minutes: number | null;
  } | null;
}

/**
 * A program_day_overrides row — one cell of a multi-cycle program that
 * deviates from the pattern in program_days. Shape matches the resolver's
 * ProgramDayOverride (src/lib/program-resolver.ts). Typegen hasn't run
 * against the new table yet, so we hand-type it here (same pattern as the
 * C2 columns below).
 */
export interface ProgramDayOverride {
  id: string;
  program_id: string;
  cycle_index: number;
  day_in_cycle: number;
  workout_template_id: string | null;
  is_rest_day: boolean;
  name: string | null;
  reason?: string | null;
  workout_template?: {
    id: string;
    name: string;
    estimated_duration_minutes: number | null;
  } | null;
}

export interface Program {
  id: string;
  name: string;
  description: string | null;
  days_per_cycle: number;
  user_id: string;
  organization_id: string | null;
  created_at: string;
  updated_at: string;
  program_days?: ProgramDay[];
  // Pattern+overrides model (2026-05-25). intended_cycles set = pattern
  // model; null/absent = legacy flat model. overrides resolved against
  // (cycle_index, day_in_cycle) by program-resolver. Typegen hasn't run
  // against the new columns/table yet — hand-typed here.
  intended_cycles?: number | null;
  overrides?: ProgramDayOverride[];
}

export interface ClientProgramAssignment {
  id: string;
  client_id: string;
  program_id: string;
  status: "active" | "paused" | "completed";
  current_day: number;
  started_at: string;
  // C2 (PR #117) added these. Typegen hasn't run yet; we type them here so
  // the rest of the codebase doesn't need `as any` everywhere.
  started_on?: string | null;
  last_advanced_at?: string | null;
  // Pattern model: completed cycles. Bumped by advance_program_day on wrap.
  cycles_completed?: number | null;
  program?: Program;
}

/**
 * Normalize the embedded program_day_overrides rows: lift the joined
 * workout_templates into `workout_template` (matching how program_days is
 * shaped). Returns [] when the embed is absent (legacy programs / RLS hid
 * the rows) so callers can treat it as "no overrides" safely.
 */
function mapOverrides(raw: any): ProgramDayOverride[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((o: any) => ({
    id: o.id,
    program_id: o.program_id,
    cycle_index: o.cycle_index,
    day_in_cycle: o.day_in_cycle,
    workout_template_id: o.workout_template_id ?? null,
    is_rest_day: o.is_rest_day ?? false,
    name: o.name ?? null,
    reason: o.reason ?? null,
    workout_template: o.workout_templates ?? null,
  }));
}

export function usePrograms() {
  const { organization, loading: orgLoading } = useOrganization();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPrograms = useCallback(async () => {
    try {
      // Org still resolving — stay in loading state so the page renders a
      // skeleton, NOT the "No programs yet" empty state. Without this, navigating
      // back to /programs after Ship It briefly shows the empty state because
      // the OrganizationContext hasn't re-resolved yet (we'd set loading=false
      // with an empty array, then re-fetch once the org arrived).
      if (!organization?.id) {
        // Only mark as "loaded with no programs" once the org context has
        // genuinely finished resolving (orgLoading=false AND organization=null
        // — e.g. a trainer with no org membership, very rare).
        if (!orgLoading) {
          setPrograms([]);
          setLoading(false);
        }
        return;
      }

      // LIST query — deliberately shallow (disk-IO reduction, 2026-08-28).
      //
      // This previously embedded the FULL day tree + every program_day_override
      // + a joined workout_templates row for BOTH, for EVERY program in the org,
      // on two pages (Programs.tsx and Workouts.tsx). The list renders only the
      // first 5 days as name/rest chips plus a count -- and it never read the
      // overrides at all. We were paying a 3-level join across four tables to
      // draw five badges.
      //
      // Now: day rows carry only what a chip needs, no nested template join, no
      // overrides. `useProgram` (the builder's detail fetch, below) keeps the
      // full shape -- depth belongs on the page that edits ONE program, not the
      // page that lists all of them.
      const { data, error } = await supabase
        .from("programs")
        .select(`
          *,
          program_days (
            id,
            day_number,
            name,
            workout_template_id,
            is_rest_day
          )
        `)
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform to match our interface. The list query no longer selects
      // workout_templates or program_day_overrides, so there is nothing to
      // lift: `workout_template` stays undefined and `overrides` is []. The
      // list only needs day_number/name/is_rest_day for its chips. Anything
      // that needs the resolved workout or per-cycle overrides must use
      // useProgram (the detail fetch), not this hook.
      const transformedData = (data || []).map((program: any) => ({
        ...program,
        program_days: [...(program.program_days ?? [])].sort(
          (a: ProgramDay, b: ProgramDay) => a.day_number - b.day_number,
        ),
        overrides: [] as ProgramDayOverride[],
      }));

      setPrograms(transformedData);
    } catch (error) {
      console.error("Error fetching programs:", error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, orgLoading]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  return { programs, loading, refetch: fetchPrograms };
}

export function useProgram(programId: string | undefined) {
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProgram = useCallback(async () => {
    if (!programId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("programs")
        .select(`
          *,
          program_days (
            id,
            day_number,
            name,
            workout_template_id,
            is_rest_day,
            workout_templates:workout_template_id (
              id,
              name,
              estimated_duration_minutes
            )
          ),
          program_day_overrides (
            id,
            program_id,
            cycle_index,
            day_in_cycle,
            workout_template_id,
            is_rest_day,
            name,
            reason,
            workout_templates:workout_template_id (
              id,
              name,
              estimated_duration_minutes
            )
          )
        `)
        .eq("id", programId)
        .single();

      if (error) throw error;

      // Transform the data
      const transformedData = {
        ...data,
        program_days: data.program_days?.map((day: any) => ({
          ...day,
          workout_template: day.workout_templates,
        })).sort((a: ProgramDay, b: ProgramDay) => a.day_number - b.day_number),
        overrides: mapOverrides((data as any).program_day_overrides),
      };

      setProgram(transformedData);
    } catch (error) {
      console.error("Error fetching program:", error);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  return { program, loading, refetch: fetchProgram };
}

export function useClientProgramAssignment(clientId: string | undefined) {
  const [assignment, setAssignment] = useState<ClientProgramAssignment | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAssignment = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("client_program_assignments")
        .select(`
          *,
          programs:program_id (
            *,
            program_days (
              id,
              day_number,
              name,
              workout_template_id,
              is_rest_day,
              workout_templates:workout_template_id (
                id,
                name,
                estimated_duration_minutes
              )
            ),
            program_day_overrides (
              id,
              program_id,
              cycle_index,
              day_in_cycle,
              workout_template_id,
              is_rest_day,
              name,
              reason,
              workout_templates:workout_template_id (
                id,
                name,
                estimated_duration_minutes
              )
            )
          )
        `)
        .eq("client_id", clientId)
        .eq("status", "active")
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Transform the data
        const transformedData = {
          ...data,
          program: data.programs ? {
            ...data.programs,
            program_days: data.programs.program_days?.map((day: any) => ({
              ...day,
              workout_template: day.workout_templates,
            })).sort((a: ProgramDay, b: ProgramDay) => a.day_number - b.day_number),
            overrides: mapOverrides((data.programs as any).program_day_overrides),
          } : undefined,
        };
        setAssignment(transformedData as ClientProgramAssignment);
      } else {
        setAssignment(null);
      }
    } catch (error) {
      console.error("Error fetching client program assignment:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  return { assignment, loading, refetch: fetchAssignment };
}
